import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Pencil, Trash2, Plus, LogIn, LogOut, Package, Eye, EyeOff, Tag } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryOptions = [
  { value: 'candles', label: 'Candles' },
  { value: 'wax_melts', label: 'Wax Melts' },
  { value: 'room_sprays', label: 'Room & Auto Sprays' },
  { value: 'skincare', label: 'Skincare' },
  { value: 'lipgloss', label: 'Lip Gloss' },
];

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.authenticated) {
        onLogin(password);
        toast.success('Welcome to the admin dashboard');
      }
    } catch (e) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-28 flex items-center justify-center" data-testid="admin-login">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm px-4"
      >
        <div className="bg-[#141414] border border-[#2A2520] rounded-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#C4A35A]/10 flex items-center justify-center border border-[#C4A35A]/20">
              <LogIn size={24} className="text-[#C4A35A]" />
            </div>
            <h1 className="font-['Cormorant_Garamond'] text-2xl text-[#E8D5A3]">Admin Dashboard</h1>
            <p className="text-xs text-[#9A8E7A] mt-1">Enter your admin password</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-full border-[#2A2520] bg-[#0A0A0A] text-[#F0E6D0] placeholder:text-[#9A8E7A]/50 focus:border-[#C4A35A] focus:ring-[#C4A35A] h-12 px-6"
              data-testid="admin-password-input"
            />
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C4A35A] hover:bg-[#D4B86A] text-[#0A0A0A] rounded-full h-12 font-bold"
              data-testid="admin-login-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

function ProductForm({ product, onSave, onCancel, adminPassword }) {
  const [form, setForm] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || '',
    category: product?.category || 'candles',
    images: product?.images?.join('\n') || '',
    in_stock: product?.in_stock !== false,
    featured: product?.featured || false,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) {
      toast.error('Name and price are required');
      return;
    }
    setSaving(true);
    try {
      const images = form.images.split('\n').map(s => s.trim()).filter(Boolean);
      const productData = {
        name: form.name,
        description: form.description,
        price: parseFloat(form.price),
        category: form.category,
        images,
        in_stock: form.in_stock,
        featured: form.featured,
      };

      if (product) {
        await axios.put(`${API}/admin/products/${product.id}`, {
          password: adminPassword,
          product: productData,
        });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/products`, {
          password: adminPassword,
          product: productData,
        });
        toast.success('Product created');
      }
      onSave();
    } catch (e) {
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
      <div>
        <label className="text-xs text-[#9A8E7A] mb-1 block">Product Name *</label>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="rounded-lg border-[#2A2520] bg-[#0A0A0A] text-[#F0E6D0] placeholder:text-[#9A8E7A]/50 focus:border-[#C4A35A]"
          placeholder="Product name"
          data-testid="form-product-name"
        />
      </div>
      <div>
        <label className="text-xs text-[#9A8E7A] mb-1 block">Description</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full rounded-lg border border-[#2A2520] bg-[#0A0A0A] text-[#F0E6D0] placeholder:text-[#9A8E7A]/50 focus:border-[#C4A35A] focus:ring-1 focus:ring-[#C4A35A] p-3 text-sm min-h-[80px] resize-y"
          placeholder="Product description"
          data-testid="form-product-description"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-[#9A8E7A] mb-1 block">Price ($) *</label>
          <Input
            type="number"
            step="0.01"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            className="rounded-lg border-[#2A2520] bg-[#0A0A0A] text-[#F0E6D0] focus:border-[#C4A35A]"
            placeholder="0.00"
            data-testid="form-product-price"
          />
        </div>
        <div>
          <label className="text-xs text-[#9A8E7A] mb-1 block">Category</label>
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full h-10 rounded-lg border border-[#2A2520] bg-[#0A0A0A] text-[#F0E6D0] px-3 text-sm focus:border-[#C4A35A] focus:ring-1 focus:ring-[#C4A35A]"
            data-testid="form-product-category"
          >
            {categoryOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs text-[#9A8E7A] mb-1 block">Image URLs (one per line)</label>
        <textarea
          value={form.images}
          onChange={(e) => setForm({ ...form, images: e.target.value })}
          className="w-full rounded-lg border border-[#2A2520] bg-[#0A0A0A] text-[#F0E6D0] placeholder:text-[#9A8E7A]/50 focus:border-[#C4A35A] focus:ring-1 focus:ring-[#C4A35A] p-3 text-sm min-h-[60px] resize-y"
          placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
          data-testid="form-product-images"
        />
      </div>
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-sm text-[#9A8E7A] cursor-pointer">
          <input
            type="checkbox"
            checked={form.in_stock}
            onChange={(e) => setForm({ ...form, in_stock: e.target.checked })}
            className="rounded border-[#2A2520] bg-[#0A0A0A] text-[#C4A35A] focus:ring-[#C4A35A]"
            data-testid="form-in-stock"
          />
          In Stock
        </label>
        <label className="flex items-center gap-2 text-sm text-[#9A8E7A] cursor-pointer">
          <input
            type="checkbox"
            checked={form.featured}
            onChange={(e) => setForm({ ...form, featured: e.target.checked })}
            className="rounded border-[#2A2520] bg-[#0A0A0A] text-[#C4A35A] focus:ring-[#C4A35A]"
            data-testid="form-featured"
          />
          Featured
        </label>
      </div>
      <div className="flex gap-3 pt-2">
        <Button
          type="submit"
          disabled={saving}
          className="flex-1 bg-[#C4A35A] hover:bg-[#D4B86A] text-[#0A0A0A] rounded-full h-10 font-bold text-sm"
          data-testid="form-save-btn"
        >
          {saving ? 'Saving...' : (product ? 'Update Product' : 'Create Product')}
        </Button>
        <Button
          type="button"
          onClick={onCancel}
          variant="outline"
          className="rounded-full h-10 border-[#2A2520] text-[#9A8E7A] hover:bg-[#1E1E1E] hover:text-[#F0E6D0] bg-transparent"
          data-testid="form-cancel-btn"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default function AdminPage() {
  const [adminPassword, setAdminPassword] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [activeTab, setActiveTab] = useState('products');
  const [promos, setPromos] = useState([]);
  const [newPromoCode, setNewPromoCode] = useState('');
  const [newPromoDiscount, setNewPromoDiscount] = useState('');

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = filterCategory ? `?category=${filterCategory}` : '';
      const res = await axios.get(`${API}/products${params}`);
      setProducts(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchPromos = async () => {
    try {
      const res = await axios.get(`${API}/admin/promos?password=${adminPassword}`);
      setPromos(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (adminPassword) {
      fetchProducts();
      fetchPromos();
    }
  }, [adminPassword, filterCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/products/${deleteTarget.id}?password=${adminPassword}`);
      toast.success('Product deleted');
      setDeleteTarget(null);
      fetchProducts();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleSave = () => {
    setShowForm(false);
    setEditProduct(null);
    fetchProducts();
  };

  const handleCreatePromo = async () => {
    if (!newPromoCode.trim() || !newPromoDiscount) {
      toast.error('Enter a code and discount %');
      return;
    }
    try {
      await axios.post(`${API}/admin/promos?password=${adminPassword}&code=${newPromoCode.trim()}&discount_percent=${parseFloat(newPromoDiscount)}`);
      toast.success(`Promo code "${newPromoCode.toUpperCase()}" created!`);
      setNewPromoCode('');
      setNewPromoDiscount('');
      fetchPromos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create promo');
    }
  };

  const handleTogglePromo = async (promo) => {
    try {
      await axios.put(`${API}/admin/promos/${promo.id}?password=${adminPassword}&active=${!promo.active}`);
      fetchPromos();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleDeletePromo = async (promoId) => {
    try {
      await axios.delete(`${API}/admin/promos/${promoId}?password=${adminPassword}`);
      toast.success('Promo deleted');
      fetchPromos();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  if (!adminPassword) {
    return <AdminLogin onLogin={setAdminPassword} />;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-28 sm:pt-32 pb-16" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl text-[#E8D5A3]" data-testid="admin-title">Admin Dashboard</h1>
            <p className="text-sm text-[#9A8E7A] mt-1">{products.length} products</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => { setEditProduct(null); setShowForm(true); }}
              className="bg-[#C4A35A] hover:bg-[#D4B86A] text-[#0A0A0A] rounded-full px-6 h-10 font-bold text-sm"
              data-testid="add-product-btn"
            >
              <Plus size={16} className="mr-2" /> Add Product
            </Button>
            <Button
              onClick={() => setAdminPassword(null)}
              variant="outline"
              className="rounded-full h-10 border-[#2A2520] text-[#9A8E7A] hover:bg-[#1E1E1E] bg-transparent"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-[#2A2520]" data-testid="admin-tabs">
          <button
            onClick={() => setActiveTab('products')}
            className={`pb-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'products' ? 'border-[#C4A35A] text-[#C4A35A]' : 'border-transparent text-[#9A8E7A] hover:text-[#F0E6D0]'
            }`}
            data-testid="tab-products"
          >
            Products ({products.length})
          </button>
          <button
            onClick={() => setActiveTab('promos')}
            className={`pb-3 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${
              activeTab === 'promos' ? 'border-[#C4A35A] text-[#C4A35A]' : 'border-transparent text-[#9A8E7A] hover:text-[#F0E6D0]'
            }`}
            data-testid="tab-promos"
          >
            <Tag size={14} /> Promo Codes ({promos.length})
          </button>
        </div>

        {/* PROMO CODES TAB */}
        {activeTab === 'promos' && (
          <div data-testid="promos-section">
            {/* Create New Promo */}
            <div className="bg-[#141414] border border-[#2A2520] rounded-2xl p-6 mb-6">
              <h2 className="font-['Cormorant_Garamond'] text-xl text-[#E8D5A3] mb-4">Create Promo Code</h2>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  placeholder="Code (e.g. TIKTOK10)"
                  value={newPromoCode}
                  onChange={(e) => setNewPromoCode(e.target.value.toUpperCase())}
                  className="flex-1 rounded-lg border-[#2A2520] bg-[#0A0A0A] text-[#F0E6D0] placeholder:text-[#9A8E7A]/50 focus:border-[#C4A35A]"
                  data-testid="new-promo-code-input"
                />
                <Input
                  type="number"
                  placeholder="Discount %"
                  value={newPromoDiscount}
                  onChange={(e) => setNewPromoDiscount(e.target.value)}
                  className="w-32 rounded-lg border-[#2A2520] bg-[#0A0A0A] text-[#F0E6D0] placeholder:text-[#9A8E7A]/50 focus:border-[#C4A35A]"
                  data-testid="new-promo-discount-input"
                />
                <Button
                  onClick={handleCreatePromo}
                  className="bg-[#C4A35A] hover:bg-[#D4B86A] text-[#0A0A0A] rounded-full px-6 h-10 font-bold text-sm"
                  data-testid="create-promo-btn"
                >
                  <Plus size={16} className="mr-2" /> Create
                </Button>
              </div>
            </div>

            {/* Promo List */}
            <div className="space-y-3">
              {promos.length === 0 ? (
                <div className="text-center py-12">
                  <Tag size={32} className="mx-auto mb-3 text-[#2A2520]" />
                  <p className="text-sm text-[#9A8E7A]">No promo codes yet. Create one to share on TikTok!</p>
                </div>
              ) : (
                promos.map(promo => (
                  <div
                    key={promo.id}
                    className="flex items-center justify-between bg-[#141414] border border-[#2A2520] rounded-xl p-4 hover:border-[#C4A35A]/20 transition-colors"
                    data-testid={`promo-${promo.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#C4A35A]/10 flex items-center justify-center border border-[#C4A35A]/20">
                        <Tag size={16} className="text-[#C4A35A]" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#E8D5A3] tracking-wider">{promo.code}</p>
                        <p className="text-xs text-[#9A8E7A]">{promo.discount_percent}% off</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleTogglePromo(promo)}
                        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                          promo.active
                            ? 'border-green-500/30 text-green-500 hover:bg-green-500/10'
                            : 'border-red-400/30 text-red-400 hover:bg-red-400/10'
                        }`}
                        data-testid={`toggle-promo-${promo.id}`}
                      >
                        {promo.active ? 'Active' : 'Inactive'}
                      </button>
                      <button
                        onClick={() => handleDeletePromo(promo.id)}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-[#9A8E7A] hover:bg-[#1E1E1E] hover:text-red-400 transition-colors"
                        data-testid={`delete-promo-${promo.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* PRODUCTS TAB */}
        {activeTab === 'products' && (<>
        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-filters">
          <button
            onClick={() => setFilterCategory('')}
            className={`px-4 py-1.5 rounded-full text-xs transition-all ${
              !filterCategory ? 'bg-[#C4A35A] text-[#0A0A0A] font-medium' : 'border border-[#2A2520] text-[#9A8E7A] hover:border-[#C4A35A]/40'
            }`}
          >
            All
          </button>
          {categoryOptions.map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilterCategory(opt.value)}
              className={`px-4 py-1.5 rounded-full text-xs transition-all ${
                filterCategory === opt.value ? 'bg-[#C4A35A] text-[#0A0A0A] font-medium' : 'border border-[#2A2520] text-[#9A8E7A] hover:border-[#C4A35A]/40'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#141414] border border-[#2A2520] rounded-2xl p-6 mb-8"
          >
            <h2 className="font-['Cormorant_Garamond'] text-xl text-[#E8D5A3] mb-4">
              {editProduct ? 'Edit Product' : 'New Product'}
            </h2>
            <ProductForm
              product={editProduct}
              adminPassword={adminPassword}
              onSave={handleSave}
              onCancel={() => { setShowForm(false); setEditProduct(null); }}
            />
          </motion.div>
        )}

        {/* Products Table */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#C4A35A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-products-list">
            {products.map(product => (
              <div
                key={product.id}
                className="flex items-center gap-4 bg-[#141414] border border-[#2A2520] rounded-xl p-4 hover:border-[#C4A35A]/20 transition-colors"
                data-testid={`admin-product-${product.id}`}
              >
                {/* Image */}
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#1E1E1E] flex-shrink-0">
                  {product.images?.[0] ? (
                    <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package size={20} className="text-[#2A2520]" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-[#F0E6D0] truncate">{product.name}</p>
                    {product.featured && (
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#C4A35A]/10 text-[#C4A35A] border border-[#C4A35A]/20">Featured</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-[#C4A35A] font-medium">${product.price.toFixed(2)}</span>
                    <span className="text-xs text-[#9A8E7A]">
                      {categoryOptions.find(c => c.value === product.category)?.label || product.category}
                    </span>
                    <span className={`text-xs flex items-center gap-1 ${product.in_stock ? 'text-green-500' : 'text-red-400'}`}>
                      {product.in_stock ? <Eye size={10} /> : <EyeOff size={10} />}
                      {product.in_stock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => { setEditProduct(product); setShowForm(true); }}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[#9A8E7A] hover:bg-[#1E1E1E] hover:text-[#C4A35A] transition-colors"
                    data-testid={`edit-product-${product.id}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(product)}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[#9A8E7A] hover:bg-[#1E1E1E] hover:text-red-400 transition-colors"
                    data-testid={`delete-product-${product.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        </>)}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="bg-[#141414] border-[#2A2520] text-[#F0E6D0]">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-xl text-[#E8D5A3]">Delete Product</DialogTitle>
            <DialogDescription className="text-[#9A8E7A]">
              Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              onClick={() => setDeleteTarget(null)}
              variant="outline"
              className="rounded-full border-[#2A2520] text-[#9A8E7A] hover:bg-[#1E1E1E] bg-transparent"
              data-testid="cancel-delete-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="rounded-full bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
